import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Swap, CheckCircle, Warning, XCircle, Barbell, ArrowRight,
  Sparkle, Heart, Stack, Shield, Lightning, ArrowsCounterClockwise,
  FilePdf, X
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const STATUS_META = {
  go: { label: "GO", color: "#2A9D8F", icon: CheckCircle, bg: "bg-[#2A9D8F]" },
  warning: { label: "WARNING", color: "#E9C46A", icon: Warning, bg: "bg-[#E9C46A]" },
  no_go: { label: "NO-GO", color: "#D90429", icon: XCircle, bg: "bg-[#D90429]" },
};

export default function MatchValidator() {
  const [athletes, setAthletes] = useState([]);
  const [redId, setRedId] = useState("");
  const [blueId, setBlueId] = useState("");
  const [tolerance, setTolerance] = useState(2);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);

  useEffect(() => {
    api.get("/athletes").then((r) => setAthletes(r.data || [])).catch(() => {});
  }, []);

  const validate = async () => {
    if (!redId || !blueId) {
      toast.error("Sélectionne les deux athlètes");
      return;
    }
    if (redId === blueId) {
      toast.error("Choisis deux athlètes différents");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/matches/validate", {
        red_athlete_id: redId, blue_athlete_id: blueId, weight_tolerance_kg: tolerance,
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de validation");
    } finally {
      setBusy(false);
    }
  };

  const swap = () => { setRedId(blueId); setBlueId(redId); setResult(null); };
  const reset = () => { setResult(null); setRedId(""); setBlueId(""); };

  const meta = result ? STATUS_META[result.status] : null;

  return (
    <div data-testid="match-validator-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>GO / NO-GO matchmaking</>} subtitle={<>Avant un gala, vérifie en 5 secondes la compatibilité de deux athlètes : âge, classe, poids, suspension, médical, défaites par arrêt. Décision instantanée selon les règles MTO/CMTO + sécurité club.</>} />
      </div>

      <div className="px-8 lg:px-12 py-8 max-w-6xl space-y-8">

        {/* Sélection des athlètes */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="match-selectors">
          <CornerSelect color="#D90429" label="Coin ROUGE" testid="select-red"
            athletes={athletes} value={redId} onChange={(v) => { setRedId(v); setResult(null); }}
            exclude={blueId}/>
          <div className="md:col-span-1 flex justify-center">
            <button onClick={swap} title="Inverser les coins" data-testid="swap-corners-btn"
              className="hard-border p-3 hover:bg-foreground hover:text-white transition-colors">
              <Swap size={20}/>
            </button>
          </div>
          <CornerSelect color="#0A0A0A" label="Coin BLEU" testid="select-blue"
            athletes={athletes} value={blueId} onChange={(v) => { setBlueId(v); setResult(null); }}
            exclude={redId}/>
        </div>

        {/* Tolérance + bouton validation */}
        <div className="hard-border p-5 flex flex-wrap items-center gap-4 bg-secondary/30">
          <div className="flex items-center gap-3">
            <label className="ticker text-muted-foreground">Tolérance poids</label>
            <input type="number" step="0.5" min="0" max="10" value={tolerance}
              onChange={(e) => setTolerance(parseFloat(e.target.value) || 0)}
              className="hard-border px-3 py-1.5 w-20" data-testid="weight-tolerance"/>
            <span className="ticker text-muted-foreground">kg</span>
          </div>
          <div className="flex-1"/>
          {result && (
            <button onClick={reset} data-testid="reset-validator"
              className="hard-border px-4 py-2.5 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
              <ArrowsCounterClockwise size={14}/> Réinitialiser
            </button>
          )}
          <button onClick={validate} disabled={busy || !redId || !blueId} data-testid="validate-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-2.5 ticker flex items-center gap-2 transition-colors disabled:opacity-50">
            <Lightning size={16} weight="fill"/> {busy ? "Analyse en cours…" : "Valider le match"}
          </button>
        </div>

        {/* Résultat */}
        {result && (
          <div className="space-y-6 fade-up" data-testid="match-result">
            {/* Verdict */}
            <div className={`hard-border p-6 ${meta.bg} text-white flex items-center gap-4`} data-testid={`verdict-${result.status}`}>
              <meta.icon size={48} weight="fill" className="shrink-0"/>
              <div className="flex-1">
                <div className="ticker text-white/70">VERDICT</div>
                <div className="font-display font-black text-4xl tracking-tight mt-1">{meta.label}</div>
                <div className="text-white/90 mt-2 font-semibold text-lg">
                  {result.red.full_name} <span className="text-white/60">vs</span> {result.blue.full_name}
                </div>
              </div>
              {result.status !== "no_go" && (
                <button onClick={() => setPdfModal(true)} data-testid="open-pdf-modal-btn"
                  className="bg-white text-foreground hover:bg-secondary px-5 py-3 ticker flex items-center gap-2 transition-colors shrink-0">
                  <FilePdf size={18}/> Fiches de pesée PDF
                </button>
              )}
            </div>

            {/* Bloqueurs */}
            {result.blockers?.length > 0 && (
              <Section title="Bloqueurs" icon={XCircle} accent="#D90429">
                <div className="hard-border" data-testid="blockers-list">
                  {result.blockers.map((b, i) => (
                    <div key={i} className={`p-4 flex items-start gap-3 ${i < result.blockers.length - 1 ? 'border-b border-border' : ''}`}>
                      <span className="text-[#D90429] font-bold text-xl mt-0.5">×</span>
                      <div>
                        <div className="font-semibold">{b.message}</div>
                        <div className="ticker text-muted-foreground mt-0.5">{b.code}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Avertissements */}
            {result.warnings?.length > 0 && (
              <Section title="Avertissements" icon={Warning} accent="#E9C46A">
                <div className="hard-border" data-testid="warnings-list">
                  {result.warnings.map((w, i) => (
                    <div key={i} className={`p-4 flex items-start gap-3 ${i < result.warnings.length - 1 ? 'border-b border-border' : ''}`}>
                      <span className="text-[#E9C46A] font-bold text-xl mt-0.5">!</span>
                      <div>
                        <div className="font-semibold">{w.message}</div>
                        <div className="ticker text-muted-foreground mt-0.5">{w.code}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Détail des vérifications */}
            <Section title="Vérifications détaillées" icon={Stack}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 hard-border" data-testid="checks-grid">
                <CheckRow label="Sexe" left={result.checks.sex?.a || "—"} right={result.checks.sex?.b || "—"}
                  ok={result.checks.sex?.ok}/>
                <CheckRow label="Catégorie d'âge"
                  left={result.checks.age_category?.a?.label || "—"}
                  right={result.checks.age_category?.b?.label || "—"}
                  ok={result.checks.age_category?.a?.id === result.checks.age_category?.b?.id}/>
                <CheckRow label="Classe d'expérience"
                  left={(result.checks.class?.a || "—").toUpperCase()}
                  right={(result.checks.class?.b || "—").toUpperCase()}
                  sub={`Écart : ${result.checks.class?.gap}`}
                  ok={result.checks.class?.gap === 0}/>
                <CheckRow label="Poids"
                  left={result.checks.weight?.a_kg ? `${result.checks.weight.a_kg} kg` : "—"}
                  right={result.checks.weight?.b_kg ? `${result.checks.weight.b_kg} kg` : "—"}
                  sub={result.checks.weight?.diff_kg !== null && result.checks.weight?.diff_kg !== undefined ? `Δ ${result.checks.weight.diff_kg} kg` : ""}
                  ok={result.checks.weight?.diff_kg !== null && result.checks.weight?.diff_kg <= (result.checks.weight?.tolerance_kg || 2)}/>
                <CheckRow label="Suspension"
                  left={result.checks.suspension?.a ? "ACTIVE" : "Aucune"}
                  right={result.checks.suspension?.b ? "ACTIVE" : "Aucune"}
                  ok={!result.checks.suspension?.a && !result.checks.suspension?.b}/>
                <CheckRow label="Défaites consécutives par arrêt"
                  left={String(result.checks.kos_consecutifs?.a ?? 0)}
                  right={String(result.checks.kos_consecutifs?.b ?? 0)}
                  ok={(result.checks.kos_consecutifs?.a ?? 0) < 2 && (result.checks.kos_consecutifs?.b ?? 0) < 2}/>
              </div>
            </Section>

            {/* Règles communes du match */}
            {result.common_rules && (
              <Section title="Règles communes appliquées" icon={Shield}>
                <div className="hard-border p-5 space-y-3" data-testid="common-rules">
                  <div className="text-sm text-muted-foreground italic">{result.common_rules.explanation}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Stat label="Catégorie" value={result.common_rules.age_label}/>
                    <Stat label="Classe appliquée" value={result.common_rules.class_id?.toUpperCase()}/>
                    <Stat label="Format"
                      value={result.common_rules.rounds_format ? `${result.common_rules.rounds_format.rounds} × ${result.common_rules.rounds_format.minutes} min` : "—"}/>
                  </div>
                  {result.common_rules.technique_rules && (
                    <div className="text-sm bg-secondary/40 p-3 hard-border">
                      <strong>Techniques permises : </strong>
                      {result.common_rules.technique_rules.description}
                    </div>
                  )}
                  {result.common_rules.equipment && (
                    <div className="text-sm">
                      <div className="ticker text-muted-foreground mb-1">Équipement obligatoire</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                        <div>Gants : <span className="text-foreground font-medium">{result.common_rules.equipment.gloves_size}</span></div>
                        <div>Casque : <span className="text-foreground font-medium">{result.common_rules.equipment.headgear}</span></div>
                        <div>Plastron : <span className="text-foreground font-medium">{result.common_rules.equipment.body_protector}</span></div>
                        <div>Protège-tibias : <span className="text-foreground font-medium">{result.common_rules.equipment.shin_guards}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}

            <div className="text-sm text-muted-foreground">
              Référence complète : <Link to="/app/reglements" className="text-[#D90429] underline">tableau maître MTO/CMTO</Link>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!result && (
          <div className="hard-border p-12 text-center" data-testid="empty-state">
            <Swap size={48} weight="duotone" className="mx-auto text-muted-foreground mb-3"/>
            <div className="font-display text-xl font-bold">Sélectionne deux athlètes</div>
            <div className="text-sm text-muted-foreground mt-1">Le verdict s'affichera ici en moins d'une seconde.</div>
            {athletes.length === 0 && (
              <Link to="/app/pointage?tab=athletes" className="inline-flex items-center gap-2 mt-6 hard-border px-5 py-2.5 ticker hover:bg-foreground hover:text-white transition-colors" data-testid="create-athletes-link">
                <Barbell size={14}/> Ajouter des athlètes <ArrowRight size={14}/>
              </Link>
            )}
          </div>
        )}
      </div>

      {pdfModal && result && (
        <WeighInPdfModal
          athletes={[
            { id: result.red.id, name: result.red.full_name, corner: "red", opponent_name: result.blue.full_name },
            { id: result.blue.id, name: result.blue.full_name, corner: "blue", opponent_name: result.red.full_name },
          ]}
          common_rules={result.common_rules}
          onClose={() => setPdfModal(false)}
        />
      )}
    </div>
  );
}

export function WeighInPdfModal({ athletes, common_rules, onClose }) {
  const [event, setEvent] = useState({
    name: "Gala Gladiateurs du Nord V",
    date: "2026-05-16",
    location: "Val-d'Or, QC",
    fight_type: "tournoi",
  });
  const [matchNumber, setMatchNumber] = useState("");
  const [weightCategory, setWeightCategory] = useState(common_rules?.class_id ? "" : "");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const payload = {
        event,
        athletes: athletes.map((a) => ({
          athlete_id: a.id,
          corner: a.corner || null,
          opponent_name: a.opponent_name || null,
          match_number: matchNumber || null,
          weight_category: weightCategory || null,
        })),
      };
      const res = await api.post("/weigh-in/pdf", payload, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const slug = athletes.map((a) => (a.name || "").replace(/\s/g, "-").substring(0, 15)).join("_");
      link.download = `fiche_pesee_${slug}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré et téléchargé");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="pdf-modal">
      <div className="bg-background hard-border max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="hard-border-b p-5 flex items-start justify-between gap-4">
          <div>
            <div className="ticker text-muted-foreground mb-1">Validateur v2 · Génération PDF</div>
            <h3 className="font-display font-black text-xl tracking-tight">Fiche de pesée officielle</h3>
            <div className="ticker text-muted-foreground mt-1">{athletes.length} athlète{athletes.length > 1 ? 's' : ''} · 1 page chacun</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-2" data-testid="pdf-modal-close"><X size={20}/></button>
        </div>
        <div className="p-5 overflow-auto flex-1 space-y-4">
          <div>
            <div className="ticker text-muted-foreground mb-2">Événement</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={event.name} onChange={(e) => setEvent({ ...event, name: e.target.value })} placeholder="Nom de l'événement" className="hard-border px-3 py-2" data-testid="pdf-event-name"/>
              <input type="date" value={event.date} onChange={(e) => setEvent({ ...event, date: e.target.value })} className="hard-border px-3 py-2" data-testid="pdf-event-date"/>
              <input value={event.location} onChange={(e) => setEvent({ ...event, location: e.target.value })} placeholder="Lieu" className="hard-border px-3 py-2" data-testid="pdf-event-location"/>
              <select value={event.fight_type} onChange={(e) => setEvent({ ...event, fight_type: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="pdf-event-type">
                <option value="tournoi">Tournoi</option>
                <option value="single show">Single show</option>
                <option value="scrimmage">Scrimmage</option>
                <option value="championnat">Championnat</option>
              </select>
            </div>
          </div>
          <div>
            <div className="ticker text-muted-foreground mb-2">Match-making (optionnel)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={weightCategory} onChange={(e) => setWeightCategory(e.target.value)} placeholder="Catégorie de poids (ex: -63,5 kg)" className="hard-border px-3 py-2" data-testid="pdf-weight-cat"/>
              <input value={matchNumber} onChange={(e) => setMatchNumber(e.target.value)} placeholder="N° du combat" className="hard-border px-3 py-2" data-testid="pdf-match-number"/>
            </div>
          </div>
          <div className="hard-border p-4 bg-secondary/30 text-sm space-y-1">
            <div className="ticker text-muted-foreground">Athlètes inclus</div>
            {athletes.map((a, i) => (
              <div key={a.id} className="flex items-center gap-2">
                <span className={`hard-border px-2 py-0.5 ticker ${a.corner === 'red' ? 'border-[#D90429] text-[#D90429]' : 'border-foreground'}`}>
                  {a.corner === 'red' ? 'ROUGE' : a.corner === 'blue' ? 'BLEU' : `#${i + 1}`}
                </span>
                <span className="font-semibold">{a.name}</span>
                {a.opponent_name && <span className="text-muted-foreground">vs {a.opponent_name}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="hard-border-t p-4 flex items-center justify-end gap-2 bg-secondary/30">
          <button onClick={onClose} className="hard-border px-4 py-2 ticker hover:bg-foreground hover:text-white transition-colors" data-testid="pdf-cancel">Annuler</button>
          <button onClick={generate} disabled={busy} data-testid="pdf-generate-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-2 ticker flex items-center gap-2 transition-colors disabled:opacity-50">
            <FilePdf size={16}/> {busy ? "Génération…" : "Générer & télécharger"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CornerSelect({ color, label, athletes, value, onChange, exclude, testid }) {
  const selected = athletes.find((a) => a.id === value);
  return (
    <div className="md:col-span-5 hard-border p-5" style={{ borderTopWidth: "4px", borderTopColor: color }}>
      <div className="ticker mb-2" style={{ color }}>{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full hard-border px-3 py-2 bg-white" data-testid={testid}>
        <option value="">— Sélectionner un athlète —</option>
        {athletes.filter((a) => a.id !== exclude).map((a) => (
          <option key={a.id} value={a.id}>
            {a.full_name}{a.weight_kg ? ` · ${a.weight_kg} kg` : ""}{a.nb_combats_total ? ` · ${a.nb_combats_total} combats` : ""}
          </option>
        ))}
      </select>
      {selected && (
        <div className="mt-3 text-sm text-muted-foreground space-y-0.5">
          <div>Sport : <span className="text-foreground">{selected.sport}</span></div>
          {selected.sex && <div>Sexe : <span className="text-foreground">{selected.sex}</span></div>}
          {selected.weight_kg && <div>Poids : <span className="text-foreground">{selected.weight_kg} kg</span></div>}
          {selected.nb_combats_total !== undefined && <div>Combats : <span className="text-foreground">{selected.nb_combats_total}</span></div>}
          {selected.suspension_active && <div className="text-[#D90429] font-semibold">⚠ SUSPENSION ACTIVE</div>}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, accent, children }) {
  return (
    <section>
      <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2"
        style={accent ? { color: accent } : {}}>
        <Icon size={20}/> {title}
      </h2>
      {children}
    </section>
  );
}

function CheckRow({ label, left, right, sub, ok }) {
  return (
    <div className="p-4 border-r border-b border-border last:border-r-0">
      <div className="flex items-start justify-between gap-3">
        <div className="ticker text-muted-foreground">{label}</div>
        <div style={{ color: ok ? "#2A9D8F" : (ok === false ? "#D90429" : "var(--muted-foreground)") }}>
          {ok === true ? <CheckCircle size={16} weight="fill"/> : ok === false ? <XCircle size={16} weight="fill"/> : null}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div className="font-mono">{left}</div>
        <div className="font-mono text-right">{right}</div>
      </div>
      {sub && <div className="ticker text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="ticker text-muted-foreground">{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}
